import { motion } from "framer-motion";
import { useOrgSettings } from "@/lib/org-settings-context";
import {
  ArrowUpRight,
  ArrowDownRight,
  ShoppingBag,
  Utensils,
  Plane,
  Zap,
  Receipt,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface TransactionFeedItemProps {
  id: string;
  type: "CREDIT" | "DEBIT";
  amount: number;
  merchantName?: string;
  description?: string;
  createdAt: string;
  onClick?: () => void;
}

export function TransactionFeedItem({
  type,
  amount,
  merchantName,
  description,
  createdAt,
  onClick,
}: TransactionFeedItemProps) {
  const getIcon = () => {
    const text = (merchantName || description || "").toLowerCase();

    if (text.includes("shopping") || text.includes("amazon") || text.includes("flipkart")) {
      return ShoppingBag;
    }
    if (text.includes("food") || text.includes("restaurant") || text.includes("zomato")) {
      return Utensils;
    }
    if (text.includes("travel") || text.includes("flight") || text.includes("uber")) {
      return Plane;
    }
    if (text.includes("bill") || text.includes("utility") || text.includes("electricity")) {
      return Zap;
    }
    return Receipt;
  };

  const Icon = getIcon();
  const isCredit = type === "CREDIT";
  const { formatCurrency } = useOrgSettings();
  const formatAmount = (cents: number) => formatCurrency(cents);

  const getTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString("en-IN", { month: "short", day: "numeric" });
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      whileHover={{ x: 4 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={cn(
        "flex items-center gap-3 p-4 border-b cursor-pointer transition-colors",
        "hover:bg-muted/50 active:bg-primary/5"
      )}
    >
      {/* Icon */}
      <div
        className={cn(
          "flex h-11 w-11 items-center justify-center rounded-full flex-shrink-0",
          isCredit ? "bg-green-500/10" : "bg-red-500/10"
        )}
      >
        {isCredit ? (
          <ArrowDownRight className="h-5 w-5 text-green-600" />
        ) : (
          <Icon className="h-5 w-5 text-red-600" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">
          {merchantName || description || "Transaction"}
        </p>
        <p className="text-sm text-muted-foreground">{getTimeAgo(createdAt)}</p>
      </div>

      {/* Amount */}
      <div className="text-right">
        <p
          className={cn(
            "font-semibold tabular-nums",
            isCredit ? "text-green-600" : "text-foreground"
          )}
        >
          {isCredit ? "+" : ""}
          {formatAmount(amount)}
        </p>
      </div>
    </motion.div>
  );
}
